import React, { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { Case, Switch } from 'react-if'
import { Button } from '@/atoms/Button'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { AngelDialog } from '@/molecules/AngelDialog'
import { ClaimConfirmation } from '@/organisms/ClaimCollectibleButton/ClaimConfirmation'
import { useClaimContext } from '@/organisms/ClaimCollectibleButton/ClaimContext'
import { ClaimInstructions } from '@/organisms/ClaimCollectibleButton/ClaimInstructions'
import { CLAIM_COLLECTABLES_MUTATION, ClaimResponse } from '@/organisms/ClaimCollectibleButton/ClaimMutation'
import { NoClaimsAvailable } from '@/organisms/ClaimCollectibleButton/NoClaimsAvailable'
import { useSafeAnalytics } from '@/utils/analytics'
import { reportErrorToBugsnag } from '@/utils/bugsnag'
import { cLogger } from '@/utils/logging/client-logger'

interface ClaimCollectibleButtonProps {
  projectSlug?: string
}

export const ClaimCollectibleButton: React.FC<ClaimCollectibleButtonProps> = ({ projectSlug }) => {
  const { track } = useSafeAnalytics()
  const {
    isOpen,
    closeModal,
    openModal,
    claimModalState,
    setClaimModalState,
    isAwesome,
    userStatus,
    userId,
    anyClaims,
    refetch,
  } = useClaimContext()
  const [doClaim, { data: claimResponse }] = useMutation<ClaimResponse>(CLAIM_COLLECTABLES_MUTATION)

  useEffect(() => {
    if (anyClaims) {
      const hasSubToClaim = userStatus?.isChosenSubscriber && !userStatus?.isChosenSubscriberSceneClaimed
      const hasPifToClaim = userStatus?.isChosenPiffer && !userStatus?.isChosenPifferSceneClaimed

      if (hasPifToClaim || hasSubToClaim) {
        doClaim({ variables: { slug: projectSlug } }).then(refetch)
      }
    }
  }, [
    doClaim,
    projectSlug,
    refetch,
    anyClaims,
    userStatus?.isChosenSubscriber,
    userStatus?.isChosenPiffer,
    userStatus?.isChosenSubscriberSceneClaimed,
    userStatus?.isChosenPifferSceneClaimed,
  ])

  return (
    <>
      <Button
        variant="copper"
        className="w-full justify-center border-collectible-gold bg-collectible-gold md:w-fit"
        onClick={async () => {
          await openModal()
          track('Opened Claim Modal', userStatus)
        }}
      >
        <SceneIcon color1="collectible-gold" color2="white" size={18} className="mr-1" />
        <span className="whitespace-nowrap">Reserve a Scene</span>
      </Button>
      <AngelDialog className="w-[700px] max-w-full" open={isOpen} onClose={closeModal}>
        <div className="px-8">
          <Switch>
            <Case condition={claimModalState === 'instructions'}>
              <ClaimInstructions
                isLoggedIn={!!userId}
                onClaim={async () => {
                  track('Attempted Claim', userStatus)

                  try {
                    await doClaim({ variables: { slug: projectSlug } })
                    refetch()
                  } catch (e) {
                    cLogger().warn('Claim failed', e)
                    reportErrorToBugsnag(e as Error)
                    track('Claim Error', userStatus)
                  }

                  const isTotallyAwesome = isAwesome || (claimResponse?.claims && claimResponse?.claims?.length > 0)
                  setClaimModalState(isTotallyAwesome ? 'confirmation' : 'noClaims')
                }}
                onClose={() => {
                  closeModal()
                  track('Closed From Instructions', userStatus)
                }}
              />
            </Case>
            <Case condition={claimModalState === 'confirmation'}>
              <ClaimConfirmation
                onClose={() => {
                  closeModal()
                  track('Closed From Confirmation', userStatus)
                }}
                data={claimResponse}
              />
            </Case>
            <Case condition={claimModalState === 'noClaims'}>
              <NoClaimsAvailable
                onClose={() => {
                  closeModal()
                  track('Closed From No Claims Available', userStatus)
                }}
              />
            </Case>
          </Switch>
        </div>
      </AngelDialog>
    </>
  )
}
